/**
 * Game state machine.
 *
 * Manages transitions between game states and notifies listeners.
 */

import type { GameState } from '@/types';

export type StateListener = (prev: GameState, next: GameState) => void;

const VALID_TRANSITIONS: Record<GameState, GameState[]> = {
  menu: ['loading', 'playing'],
  loading: ['playing', 'menu'],
  playing: ['paused', 'dead', 'complete', 'menu'],
  paused: ['playing', 'menu'],
  dead: ['playing', 'menu'], // "playing" = restart
  complete: ['results'],
  results: ['menu', 'playing'], // "playing" = replay
};

export class StateMachine {
  private _state: GameState = 'menu';
  private listeners: StateListener[] = [];

  get state(): GameState {
    return this._state;
  }

  canTransition(to: GameState): boolean {
    return VALID_TRANSITIONS[this._state].includes(to);
  }

  transition(to: GameState): boolean {
    if (!this.canTransition(to)) {
      console.warn(`Invalid state transition: ${this._state} → ${to}`);
      return false;
    }
    const prev = this._state;
    this._state = to;
    for (const listener of this.listeners) {
      listener(prev, to);
    }
    return true;
  }

  onTransition(listener: StateListener): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  /** Force state without validation (for initialization) */
  reset(state: GameState = 'menu'): void {
    this._state = state;
  }
}
